import React, { useState } from 'react';
import Icon from '../Icon';
import { C } from '../../utils/styles';
import api from '../../utils/api';
import StyledSelect from '../StyledSelect';

const PAYROLL_PROVIDERS = [
  { id: 'adp', name: 'ADP', icon: 'business-outline' },
  { id: 'paycor', name: 'Paycor', icon: 'business-outline' },
  { id: 'gusto', name: 'Gusto', icon: 'business-outline' },
  { id: 'paylocity', name: 'Paylocity', icon: 'business-outline' },
  { id: 'paycom', name: 'Paycom', icon: 'business-outline' },
  { id: 'quickbooks', name: 'QuickBooks', icon: 'business-outline' },
  { id: 'square', name: 'Square', icon: 'business-outline' },
  { id: 'other', name: 'Other', icon: 'business-outline' },
];

export default function PayrollSetupStep({ client, onComplete }) {
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [formData, setFormData] = useState({
    paySchedule: 'biweekly',
    nextPayDate: '',
    deductionCode: 'HCPREM',
    deliveryMethod: 'portal_download',
    deliveryEmail: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  
  const hasPayroll = client?.payroll;
  
  if (hasPayroll && !selectedProvider) {
    return <PayrollConfigured payroll={client.payroll} onEdit={() => setSelectedProvider(client.payroll.provider)} onComplete={onComplete} />;
  }
  
  if (selectedProvider) {
    return (
      <PayrollConfigForm
        provider={selectedProvider}
        providerName={PAYROLL_PROVIDERS.find(p => p.id === selectedProvider)?.name}
        formData={formData}
        setFormData={setFormData}
        onBack={() => setSelectedProvider(null)}
        onSave={async () => {
          if (!formData.nextPayDate) {
            setError('Next pay date is required');
            return;
          }
          
          setSaving(true);
          setError(null);
          
          try {
            await api.clients.setupPayroll(client.id, {
              provider: selectedProvider,
              providerName: PAYROLL_PROVIDERS.find(p => p.id === selectedProvider)?.name,
              ...formData,
            });
            window.location.reload();
          } catch (err) {
            console.error('Failed to setup payroll:', err);
            setError('Failed to save payroll configuration. Please try again.');
            setSaving(false);
          }
        }}
        saving={saving}
        error={error}
      />
    );
  }
  
  return (
    <div>
      <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>
        STEP 3: Census & Payroll Integration
      </h2>
      <p style={{ fontSize: 14, color: C.textMid, marginBottom: 24 }}>
        Upload your employee census and connect your payroll system.
      </p>
      
      {/* Contact Info */}
      <div style={{ 
        background: C.bg, 
        border: `1px solid ${C.border}`, 
        borderRadius: 8, 
        padding: 16,
        marginBottom: 24 
      }}>
        <div style={{ fontSize: 12, color: C.textMid, marginBottom: 8 }}>
          Your contact for this step:
        </div>
        <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 4 }}>
          Cassie Nix · Director of Operations/Payroll
        </div>
        <div style={{ fontSize: 13, color: C.text }}>
          c.nix@healthcues.com · 678.522.9988
        </div>
      </div>
      
      <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}>
        3b. Connect Your Payroll System
      </h3>
      
      <p style={{ fontSize: 14, color: C.textMid, marginBottom: 24 }}>
        Connecting your payroll system allows us to automatically generate deduction files each pay period.
      </p>
      
      <div style={{
        background: '#fff',
        border: `1px solid ${C.border}`,
        borderRadius: 12,
        padding: 32
      }}>
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 16 }}>
          Select your payroll provider:
        </div>
        
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
          gap: 12,
        }}>
          {PAYROLL_PROVIDERS.map(provider => (
            <button
              key={provider.id}
              onClick={() => setSelectedProvider(provider.id)}
              style={{
                padding: '20px',
                background: C.bg,
                border: `1px solid ${C.border}`,
                borderRadius: 8,
                cursor: 'pointer',
                textAlign: 'center',
                transition: 'all 0.15s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = C.blueLight;
                e.currentTarget.style.borderColor = C.blue;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = C.bg;
                e.currentTarget.style.borderColor = C.border;
              }}
            >
              <Icon name={provider.icon} style={{ fontSize: 32, marginBottom: 8, color: C.textMid }} />
              <div style={{ fontSize: 13, fontWeight: 600 }}>{provider.name}</div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function PayrollConfigForm({ provider, providerName, formData, setFormData, onBack, onSave, saving, error }) {
  return (
    <div>
      <button
        onClick={onBack}
        style={{
          background: 'transparent',
          border: 'none',
          color: C.blue,
          fontSize: 13,
          fontWeight: 600,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          marginBottom: 16,
          padding: 0,
        }}
      >
        <Icon name="arrow-back" style={{ fontSize: 16 }} />
        Back to provider selection
      </button>
      
      <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>
        Payroll Integration: {providerName}
      </h2>
      
      {error && (
        <div style={{
          background: C.redLight,
          border: `1px solid ${C.red}`,
          borderRadius: 8,
          padding: 16,
          marginBottom: 24,
          display: 'flex',
          alignItems: 'center',
          gap: 12
        }}>
          <Icon name="alert-circle" style={{ fontSize: 20, color: C.red }} />
          <span style={{ fontSize: 13, color: C.red }}>{error}</span>
        </div>
      )}
      
      <div style={{
        background: '#fff',
        border: `1px solid ${C.border}`,
        borderRadius: 12,
        padding: 32
      }}>
        {/* API status */}
        <div style={{
          background: C.bg,
          borderRadius: 8,
          padding: 16,
          marginBottom: 24,
          display: 'flex',
          gap: 16,
        }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
              <Icon name="link-outline" style={{ fontSize: 16, color: C.textMid }} />
              API Connection
            </div>
            <div style={{ fontSize: 12, color: C.textMid }}>Coming soon</div>
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
              <Icon name="folder-outline" style={{ fontSize: 16, color: C.green }} />
              File Exchange
            </div>
            <div style={{ fontSize: 12, color: C.green, fontWeight: 600 }}>Available now</div>
          </div>
        </div>
        
        <div style={{
          fontSize: 14,
          fontWeight: 700,
          marginBottom: 16,
          paddingBottom: 12,
          borderBottom: `1px solid ${C.border}`
        }}>
          FILE-BASED SETUP
        </div>
        
        <div style={{ display: 'grid', gap: 20 }}>
          {/* Pay Schedule */}
          <div>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
              Pay Schedule *
            </label>
            <StyledSelect
              value={formData.paySchedule}
              onValueChange={(val) => setFormData({ ...formData, paySchedule: val })}
              options={[
                { value: "weekly", label: "Weekly" },
                { value: "biweekly", label: "Biweekly" },
                { value: "semimonthly", label: "Semi-monthly" },
                { value: "monthly", label: "Monthly" }
              ]}
            />
          </div>
          
          {/* Next Pay Date */}
          <div>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
              Next Pay Date *
            </label>
            <input
              type="date"
              value={formData.nextPayDate}
              onChange={(e) => setFormData({ ...formData, nextPayDate: e.target.value })}
              style={{
                width: '100%',
                padding: '10px 14px',
                fontSize: 14,
                border: `1px solid ${C.border}`,
                borderRadius: 6,
                boxSizing: 'border-box',
              }}
            />
          </div>
          
          {/* Deduction Code */}
          <div>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
              Deduction Code
            </label>
            <input
              type="text"
              value={formData.deductionCode}
              onChange={(e) => setFormData({ ...formData, deductionCode: e.target.value.toUpperCase() })}
              placeholder="e.g., HCPREM"
              style={{
                width: '100%',
                padding: '10px 14px',
                fontSize: 14,
                border: `1px solid ${C.border}`,
                borderRadius: 6,
                boxSizing: 'border-box',
              }}
            />
            <div style={{ fontSize: 12, color: C.textMid, marginTop: 4 }}>
              Your payroll deduction code for HealthCues premiums
            </div>
          </div>
          
          {/* File Format */}
          <div>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
              File Format
            </label>
            <div style={{
              padding: '10px 14px',
              fontSize: 14,
              background: C.bg,
              border: `1px solid ${C.border}`,
              borderRadius: 6,
            }}>
              {providerName} Standard Import (.csv)
            </div>
          </div>
          
          {/* Delivery Method */}
          <div>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
              Delivery Method
            </label>
            <StyledSelect
              value={formData.deliveryMethod}
              onValueChange={(val) => setFormData({ ...formData, deliveryMethod: val })}
              options={[
                { value: "portal_download", label: "Download from portal" },
                { value: "email", label: "Email to payroll admin" },
                { value: "sftp", label: "SFTP (requires setup)" }
              ]}
            />
          </div>
          
          {formData.deliveryMethod === 'email' && (
            <div>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
                Payroll Admin Email
              </label>
              <input
                type="email"
                value={formData.deliveryEmail}
                onChange={(e) => setFormData({ ...formData, deliveryEmail: e.target.value })}
                placeholder="payroll@company.com"
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  fontSize: 14,
                  border: `1px solid ${C.border}`,
                  borderRadius: 6,
                  boxSizing: 'border-box',
                }}
              />
            </div>
          )}
        </div>
        
        {/* How it works */}
        <div style={{
          marginTop: 24,
          padding: 20,
          background: C.bg,
          borderRadius: 8,
        }}>
          <div style={{
            fontSize: 13,
            fontWeight: 700,
            marginBottom: 12,
            paddingBottom: 8,
            borderBottom: `1px solid ${C.border}`
          }}>
            HOW IT WORKS
          </div>
          <div style={{ fontSize: 13, lineHeight: 1.7, color: C.text }}>
            <div style={{ marginBottom: 8 }}>Before each pay period:</div>
            <ol style={{ paddingLeft: 20, margin: 0 }}>
              <li>HealthCues generates a deduction file with the correct pre-tax premium amounts for each employee</li>
              <li>You download the file from this portal (or receive it via email)</li>
              <li>Import the file into {providerName} before running payroll</li>
              <li>After payroll runs, upload the payroll register here for reconciliation</li>
            </ol>
          </div>
        </div>
        
        {/* Actions */}
        <div style={{ display: 'flex', gap: 12, marginTop: 24 }}>
          <button
            type="button"
            onClick={onBack}
            style={{
              flex: 1,
              padding: '12px',
              background: 'transparent',
              color: C.text,
              border: `1px solid ${C.border}`,
              borderRadius: 8,
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Cancel
          </button>
          
          <button
            type="button"
            onClick={onSave}
            disabled={saving}
            style={{
              flex: 1,
              padding: '12px',
              background: saving ? C.textLight : C.blue,
              color: 'white',
              border: 'none',
              borderRadius: 8,
              fontSize: 14,
              fontWeight: 700,
              cursor: saving ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
            }}
          >
            <Icon name="save-outline" style={{ fontSize: 16 }} />
            {saving ? 'Saving...' : 'Save Payroll Setup'}
          </button>
        </div>
      </div>
    </div>
  );
}

function PayrollConfigured({ payroll, onEdit, onComplete }) {
  const scheduleLabel = {
    weekly: 'Weekly',
    biweekly: 'Biweekly',
    semimonthly: 'Semi-monthly',
    monthly: 'Monthly',
  }[payroll.paySchedule] || payroll.paySchedule;
  
  const deliveryLabel = {
    portal_download: 'Download from portal',
    email: 'Email to payroll admin',
    sftp: 'SFTP',
  }[payroll.deliveryMethod] || payroll.deliveryMethod;
  
  return (
    <div>
      <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>
        3b. Payroll Integration
      </h2>
      
      <div style={{
        background: C.greenLight,
        border: `1px solid ${C.green}`,
        borderRadius: 12,
        padding: 32
      }}>
        <div style={{ display: 'flex', alignItems: 'start', gap: 16, marginBottom: 24 }}>
          <Icon name="checkmark-circle" style={{ fontSize: 48, color: C.green }} />
          <div style={{ flex: 1 }}>
            <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>
              Payroll Setup Complete
            </h3>
            <p style={{ fontSize: 13, color: C.textMid, marginBottom: 0 }}>
              Your payroll integration is configured and ready to generate deduction files.
            </p>
          </div>
        </div>
        
        <div style={{ 
          background: 'white', 
          borderRadius: 8, 
          padding: 20,
          marginBottom: 24 
        }}>
          <div style={{ display: 'grid', gap: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ fontSize: 13, color: C.textMid }}>Provider:</span>
              <span style={{ fontSize: 13, fontWeight: 600 }}>{payroll.providerName}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ fontSize: 13, color: C.textMid }}>Pay Schedule:</span>
              <span style={{ fontSize: 13, fontWeight: 600 }}>{scheduleLabel}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ fontSize: 13, color: C.textMid }}>Next Pay Date:</span>
              <span style={{ fontSize: 13, fontWeight: 600 }}>{new Date(payroll.nextPayDate).toLocaleDateString()}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ fontSize: 13, color: C.textMid }}>Deduction Code:</span>
              <span style={{ fontSize: 13, fontWeight: 600 }}>{payroll.deductionCode}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ fontSize: 13, color: C.textMid }}>Delivery:</span>
              <span style={{ fontSize: 13, fontWeight: 600 }}>{deliveryLabel}</span>
            </div>
          </div>
        </div>
        
        <div style={{ display: 'flex', gap: 12 }}>
          <button
            onClick={onEdit}
            style={{
              padding: '10px 20px',
              background: 'white',
              color: C.text,
              border: `1px solid ${C.border}`,
              borderRadius: 8,
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <Icon name="create-outline" style={{ fontSize: 16 }} />
            Edit Configuration
          </button>
          
          <button
            onClick={onComplete}
            style={{
              padding: '10px 20px',
              background: C.blue,
              color: 'white',
              border: 'none',
              borderRadius: 8,
              fontSize: 13,
              fontWeight: 700,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            Continue to Step 4
            <Icon name="arrow-forward" style={{ fontSize: 16 }} />
          </button>
        </div>
      </div>
    </div>
  );
}
